import { ModelSortDirection } from "~/api/API";
import { updateAppointmentNotificationStatus } from "~/api/graphql-custom/mutations";
import { portalNotificationsByRecipient } from "~/api/graphql-custom/queries";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import { graphql } from "~/lib/graphql-client";

const PORTAL_NOTIFICATION_LIMIT = 50;

export interface PortalNotification {
  id: string;
  appointmentRecordID: string;
  notificationType: string;
  title: string;
  body: string;
  status: string;
  readAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface FetchNotificationsResult {
  notifications: PortalNotification[];
  nextToken: string | null;
}

const toleratedReadAtSerializationError =
  "Unable to serialize `` as a valid DateTime Object.";

const hasNonToleratedReadAtErrors = (
  fatalErrors: Array<{ path?: unknown; message?: string }>,
): boolean => {
  return fatalErrors.some((error) => {
    const pathEndsWithReadAt =
      Array.isArray(error.path) &&
      error.path[error.path.length - 1] === "readAt";
    return !(
      pathEndsWithReadAt &&
      typeof error.message === "string" &&
      error.message.includes(toleratedReadAtSerializationError)
    );
  });
};

/**
 * Fetch portal notifications for a given recipient, most recent first.
 * Uses the byRecipientAndStatus GSI.
 */
export const fetchPortalNotifications = async (
  recipientID: string,
  limit: number = PORTAL_NOTIFICATION_LIMIT,
  nextToken?: string | null,
): Promise<FetchNotificationsResult> => {
  const response = await graphql(
    {
      query: portalNotificationsByRecipient,
      variables: {
        recipientID,
        sortDirection: ModelSortDirection.DESC,
        limit,
        ...(nextToken ? { nextToken } : {}),
      },
    },
    { errorMode: "silent" },
  );

  if (hasNonToleratedReadAtErrors(response.fatalErrors)) {
    throw new Error(
      `fetchPortalNotifications failed: ${JSON.stringify(response.fatalErrors)}`,
    );
  }

  const connection =
    response.data?.appointmentNotificationsByRecipientAndStatus;
  const items = (connection?.items ?? []).filter(
    (item): item is NonNullable<typeof item> => item !== null,
  ) as PortalNotification[];

  return {
    notifications: items,
    nextToken: connection?.nextToken ?? null,
  };
};

/**
 * Mark a single notification as READ.
 */
export const markNotificationAsRead = async (
  notificationId: string,
): Promise<void> => {
  const readAt = new Date().toISOString();

  await amplifyGraphQLClient.graphql({
    query: updateAppointmentNotificationStatus,
    variables: {
      input: { id: notificationId, readAt },
    },
  });
};

/**
 * Mark a single notification as UNREAD by clearing `readAt`.
 *
 * The notification's UI status (`READ` / `UNREAD`) is derived from the
 * presence of `readAt`, so setting it to `null` flips the item back to the
 * unread state. The stored `status` column is intentionally left untouched —
 * it is a deprecated channel-delivery field and is not used by the portal UI.
 */
export const markNotificationAsUnread = async (
  notificationId: string,
): Promise<void> => {
  await amplifyGraphQLClient.graphql({
    query: updateAppointmentNotificationStatus,
    variables: {
      input: { id: notificationId, readAt: null },
    },
  });
};

/**
 * Mark all UNREAD notifications as READ for a recipient.
 * Fetches unread items first, then batch-updates them.
 */
export const markAllNotificationsAsRead = async (
  recipientID: string,
): Promise<void> => {
  // Fetch all notifications pages and determine unread by readAt
  const unreadIds: string[] = [];
  let nextToken: string | null = null;

  do {
    const response = await fetchPortalNotifications(
      recipientID,
      100,
      nextToken,
    );
    unreadIds.push(
      ...response.notifications
        .filter((item) => !item.readAt)
        .map((item) => item.id),
    );
    nextToken = response.nextToken;
  } while (nextToken);

  if (unreadIds.length === 0) return;

  const readAt = new Date().toISOString();

  // Update all in parallel and retain partial success details.
  const results = await Promise.allSettled(
    unreadIds.map((id) =>
      amplifyGraphQLClient.graphql({
        query: updateAppointmentNotificationStatus,
        variables: { input: { id, readAt } },
      }),
    ),
  );

  const failedIds = results
    .map((result, index) => ({ result, id: unreadIds[index] }))
    .filter(
      (entry): entry is { result: PromiseRejectedResult; id: string } =>
        entry.result.status === "rejected",
    )
    .map((entry) => entry.id);

  if (failedIds.length > 0) {
    throw new Error(
      `markAllNotificationsAsRead partially failed for notification IDs: ${failedIds.join(", ")}`,
    );
  }
};
